import React, { useEffect, useState } from "react";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { useTranslation } from "react-i18next";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import Show from "./Show";

interface ImageItem {
 url: string;
 alt?: string;
 type?: "image" | "video";
}

interface ImageZoomPanProps {
 src: string;
 alt: string;
 className?: string;
 maxScale?: number;
 minScale?: number;
 initialScale?: number;
 onClose: () => void;
 isOpen: boolean;
 images?: ImageItem[];
 currentIndex?: number;
 onIndexChange?: (index: number) => void;
}

const ImageZoomPan: React.FC<ImageZoomPanProps> = ({
 src,
 alt,
 className = "",
 maxScale = 4,
 minScale = 0.5,
 initialScale = 1,
 onClose,
 isOpen = false,
 images = [],
 currentIndex = 0,
 onIndexChange,
}) => {
 const { t } = useTranslation();
 // Use all images and videos for navigation (no filtering)
 const mediaItems = images.length > 0 ? images : [{ url: src, alt, type: "image" as "image" | "video" }];
 
 const [currentMediaIndex, setCurrentMediaIndex] = useState(() => {
  return currentIndex >= 0 && currentIndex < mediaItems.length ? currentIndex : 0;
 });
 
 // Update current index when prop changes
 useEffect(() => {
  if (currentIndex >= 0 && currentIndex < mediaItems.length) {
   setCurrentMediaIndex(currentIndex);
  }
 }, [currentIndex, mediaItems.length]);

 // Get current media from gallery or use src prop
 const currentMedia = mediaItems[currentMediaIndex] || { url: src, alt, type: "image" as "image" | "video" };
 const isVideo = currentMedia.type === "video" || (!currentMedia.type && currentMedia.url.toLowerCase().match(/\.(mp4|webm|ogg|mov|avi|mkv)$/i));

 // Navigation handlers
 const hasPrevious = mediaItems.length > 1 && currentMediaIndex > 0;
 const hasNext = mediaItems.length > 1 && currentMediaIndex < mediaItems.length - 1;

 const handlePrevious = () => {
  if (hasPrevious) {
   const newIndex = currentMediaIndex - 1;
   setCurrentMediaIndex(newIndex);
   onIndexChange?.(newIndex);
  }
 };

 const handleNext = () => {
  if (hasNext) {
   const newIndex = currentMediaIndex + 1;
   setCurrentMediaIndex(newIndex);
   onIndexChange?.(newIndex);
  }
 };

 // Close on Escape key and handle keyboard navigation
 useEffect(() => {
  if (!isOpen) return;
  const handleKeyDown = (e: KeyboardEvent) => {
   if (e.key === "Escape") {
    onClose();
   } else if (e.key === "ArrowLeft" && hasPrevious) {
    e.preventDefault();
    const newIndex = currentMediaIndex - 1;
    setCurrentMediaIndex(newIndex);
    onIndexChange?.(newIndex);
   } else if (e.key === "ArrowRight" && hasNext) {
    e.preventDefault();
    const newIndex = currentMediaIndex + 1;
    setCurrentMediaIndex(newIndex);
    onIndexChange?.(newIndex);
   }
  };

  document.body.style.overflow = "hidden";
  document.addEventListener("keydown", handleKeyDown);

  return () => {
   document.body.style.overflow = "";
   document.removeEventListener("keydown", handleKeyDown);
  };
 }, [isOpen, onClose, hasPrevious, hasNext, currentMediaIndex, onIndexChange]);
 return (
  <Show ifcond={isOpen}>
   <div className="fixed inset-0 bg-black/90 z-50 flex flex-col" dir="ltr">
    {/* Header with close button and media counter */}
    <div className="flex justify-between items-center p-4 bg-black/80 text-white">
     <div className="flex items-center gap-4">
      <div className="text-lg font-medium">{currentMedia.alt || alt}</div>
      {mediaItems.length > 1 && (
       <div className="text-sm text-gray-300">
        {currentMediaIndex + 1} / {mediaItems.length}
       </div>
      )}
     </div>
     <button
      onClick={onClose}
      className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded transition-colors"
     >
      {t("imageZoom.close")}
     </button>
    </div>

    {/* Media container */}
    <div className="flex-1 relative overflow-hidden">
     {isVideo ? (
      <>
       {/* Previous button */}
       {hasPrevious && (
        <button
         onClick={(e) => {
          e.stopPropagation();
          handlePrevious();
         }}
         className="absolute left-4 top-1/2 -translate-y-1/2 z-10 p-3 bg-black/70 hover:bg-black/90 rounded-full text-white transition-colors"
         title={t("imageZoom.previous") || "Previous (←)"}
        >
         <FaChevronLeft className="w-6 h-6" />
        </button>
       )}

       {/* Next button */}
       {hasNext && (
        <button
         onClick={(e) => {
          e.stopPropagation();
          handleNext();
         }}
         className="absolute right-4 top-1/2 -translate-y-1/2 z-10 p-3 bg-black/70 hover:bg-black/90 rounded-full text-white transition-colors"
         title={t("imageZoom.next") || "Next (→)"}
        >
         <FaChevronRight className="w-6 h-6" />
        </button>
       )}

       {/* Video player */}
       <div className="w-full h-full flex items-center justify-center p-4">
        <video
         key={currentMedia.url}
         src={currentMedia.url}
         controls
         preload="metadata"
         className="max-w-full max-h-[90vh] object-contain"
        >
         {t("productDetail.videoNotSupported") || "Your browser does not support video playback"}
        </video>
       </div>
      </>
     ) : (
      <TransformWrapper
       key={currentMedia.url}
       initialScale={initialScale}
       minScale={minScale}
       maxScale={maxScale}
       centerOnInit
       wheel={{ step: 0.1 }}
       doubleClick={{ step: 0.5 }}
       pinch={{ step: 5 }}
      >
       {({ zoomIn, zoomOut, resetTransform }) => (
        <>
         <div className="absolute right-4 top-4 z-10 flex space-x-2 bg-black/70 p-2 rounded">
          <button
           onClick={(e) => {
            e.stopPropagation();
            zoomIn();
           }}
           className="px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded text-white text-sm"
           title={t("imageZoom.zoomIn") || "Zoom In"}
          >
           {t("imageZoom.zoomIn") || "+"}
          </button>
          <button
           onClick={(e) => {
            e.stopPropagation();
            zoomOut();
           }}
           className="px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded text-white text-sm"
           title={t("imageZoom.zoomOut") || "Zoom Out"}
          >
           {t("imageZoom.zoomOut") || "-"}
          </button>
          <button
           onClick={(e) => {
            e.stopPropagation();
            resetTransform();
           }}
           className="px-3 py-1 bg-blue-600 hover:bg-blue-500 rounded text-white text-sm"
           title={t("imageZoom.reset") || "Reset"}
          >
           {t("imageZoom.reset") || "Reset"}
          </button>
         </div>

         {/* Previous button */}
         {hasPrevious && (
          <button
           onClick={(e) => {
            e.stopPropagation();
            handlePrevious();
           }}
           className="absolute left-4 top-1/2 -translate-y-1/2 z-10 p-3 bg-black/70 hover:bg-black/90 rounded-full text-white transition-colors"
           title={t("imageZoom.previous") || "Previous (←)"}
          >
           <FaChevronLeft className="w-6 h-6" />
          </button>
         )}

         {/* Next button */}
         {hasNext && (
          <button
           onClick={(e) => {
            e.stopPropagation();
            handleNext();
           }}
           className="absolute right-4 top-1/2 -translate-y-1/2 z-10 p-3 bg-black/70 hover:bg-black/90 rounded-full text-white transition-colors"
           title={t("imageZoom.next") || "Next (→)"}
          >
           <FaChevronRight className="w-6 h-6" />
          </button>
         )}

         <div className="w-full h-full flex items-center justify-center p-4">
          <TransformComponent
           wrapperClass="w-full h-full"
           contentClass="flex items-center justify-center"
          >
           <img
            src={currentMedia.url}
            alt={currentMedia.alt || alt}
            className={`max-w-full max-h-[90vh] object-contain ${className}`}
            draggable={false}
            onClick={(e) => e.stopPropagation()}
           />
          </TransformComponent>
         </div>
        </>
       )}
      </TransformWrapper>
     )}
    </div>
   </div>
  </Show>
 );
};

export default ImageZoomPan;
